import {
  BadgeGroup,
  College,
  AskLubavResponse,
  COLOR_TOKENS,
  SPACING,
  lottieAnimations,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { MakeItPersonalHeader } from '@backrs_ui/grow-ui/src/pages/College/molecules/MakeItPersonalHeader';
import {
  AskLubavUniversityBadge,
  setActiveAskLubavUniversityBadge,
} from '@backrs_ui/grow-ui/src/redux/college/college';
import { sendCollegeAskLubavPrompt } from '@backrs_ui/grow-ui/src/redux/college/college.thunks';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectProfilePhoto } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, Theme, SxProps } from '@mui/material';
import Lottie from 'lottie-react';
import Markdown from 'react-markdown';

type MakeItPersonalProps = Readonly<{
  collegeId: College['id'];
}>;

export const MakeItPersonal = ({ collegeId }: MakeItPersonalProps) => {
  const dispatch = useAppDispatch();

  const userId = useAppSelector((state) => state.user.user?.id);
  const profilePicture = useAppSelector(selectProfilePhoto);

  const { askLubavUniversityBadges, activeAskLubavUniversityBadge } = useAppSelector(
    (state) => state.college,
  );

  const loadingAnimation = (
    <Box sx={loadingAnimationSx}>
      <Lottie animationData={lottieAnimations.lubavLoader} loop={true} height={24} width={24} />
    </Box>
  );

  const errorMessage = (
    <Box sx={errorMessageSx}>
      <Markdown>{appStrings.modules.chatBox.overloadErr}</Markdown>
    </Box>
  );

  // Helper to determine if response is a valid markdown string
  const isValidResponse = (response: unknown): response is string => {
    return typeof response === 'string' && response.trim() !== '';
  };

  // Helper to determine if response is an error
  const isErrorResponse = (response: unknown): boolean => {
    return (
      response !== '' && response !== undefined && response !== null && !isValidResponse(response)
    );
  };

  const handleOpenBadge = (badge: AskLubavUniversityBadge) => {
    if (activeAskLubavUniversityBadge?.id === badge.id) {
      dispatch(setActiveAskLubavUniversityBadge(undefined));

      return;
    }

    // if there is no response yet, we call the thunk to get the response
    if (!isValidResponse(badge.response) && userId) {
      dispatch(setActiveAskLubavUniversityBadge({ ...badge, isLoading: true }));

      dispatch(sendCollegeAskLubavPrompt({ askLubavBadge: badge, collegeId, user_id: userId }));

      return;
    }

    dispatch(setActiveAskLubavUniversityBadge(badge));
  };

  return (
    <Box sx={askLubavSidebar}>
      <MakeItPersonalHeader profilePicture={profilePicture || undefined} />

      {askLubavUniversityBadges.map((badge) => (
        <BadgeGroup
          key={badge.id}
          badgeName={badge.badgeName}
          title={badge.title}
          isOpen={activeAskLubavUniversityBadge?.id === badge.id}
          setIsOpen={() => handleOpenBadge(badge)}
        >
          <Box>
            {badge.isLoading && loadingAnimation}

            {!badge.isLoading && isErrorResponse(badge.response) && errorMessage}

            {!badge.isLoading && isValidResponse(badge.response) && (
              <Box sx={askLubavTextSx}>
                <AskLubavResponse
                  response={badge.response}
                  tier={undefined}
                  apiKeyAction={{
                    key: badge.endpoint,
                  }}
                  containerSx={{
                    backgroundColor: COLOR_TOKENS['Colors/Base/White'],
                    padding: `${SPACING['spacing-none']} !important`,
                  }}
                  contentSize="SMALL"
                />
              </Box>
            )}
          </Box>
        </BadgeGroup>
      ))}
    </Box>
  );
};

const askLubavTextSx: SxProps<Theme> = ({ backrs }) => ({
  pb: backrs.spacing['spacing-xl'],
  px: backrs.spacing['spacing-lg'],
});

const errorMessageSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Error/800'],
  pb: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-lg'],
});

const loadingAnimationSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 24,
  height: 24,
  pl: backrs.spacing['spacing-md'],
});

const askLubavSidebar: SxProps<Theme> = ({ backrs }) => ({
  borderLeft: { md: `1px solid ${backrs.colors['Colors/Base/Black']}` },
  borderTop: { xs: `1px solid ${backrs.colors['Colors/Base/Black']}`, md: 'none' },
  '@media (max-width: 414px)': {
    borderLeft: 'none',
  },
  maxWidth: { md: '300px', lg: '468px' },
  objectFit: 'cover',
  height: { md: '100svh' },
  py: { xs: backrs.spacing['spacing-3xl'] },
  px: { md: backrs.spacing['spacing-3xl'] },
  m: { xs: backrs.spacing['spacing-3xl'], md: 'unset' },
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],

  overflowY: { md: 'auto' },
});
